import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, Stack, Button, IconButton, TextField, MenuItem, Chip, Divider,
  Tooltip, CircularProgress, Snackbar, ToggleButtonGroup, ToggleButton, Tab, Tabs,
  Dialog, DialogTitle, DialogContent, DialogActions, Menu, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Alert,
} from '@mui/material';
import {
  Add, Refresh, FactCheck, AccountTree, ViewList, ChevronRight, ExpandMore,
  Lock, Search, ReceiptLong, CheckCircle, WarningAmber, Cancel, KeyboardArrowDown,
  UnfoldMore, UnfoldLess, Hub, AccountBalance,
} from '@mui/icons-material';
import { RouterLink } from '../../utils/linkNav';
import { coaService, CoaAccount, CoaSummary, CoaValidation } from '../../services/coaService';
import { financeService } from '../../services/financeService';
import { EnhancedGeneralLedger } from '../../types/finance';
import { formatCurrency, formatAmount } from '../../utils/currency';
import { useControlRoomColors } from '../../components/common/controlRoom';
import { StatusDot } from '../../components/common/pageKit';

const TYPE_TINT: Record<string, string> = { ASSET: '#059669', LIABILITY: '#dc2626', EQUITY: '#7c3aed', INCOME: '#0284c7', EXPENSE: '#ea580c' };
const MODULE_TINT: Record<string, string> = { Treasury: '#0284c7', Procurement: '#059669', Sales: '#8b5cf6', Inventory: '#f59e0b', Production: '#3b82f6', GST: '#d97706', TDS: '#d97706', TCS: '#d97706', Payroll: '#14b8a6', 'Fixed Assets': '#0891b2', Finance: '#6366f1' };
const fyRange = () => { const t = new Date('2026-08-16'); const s = t.getMonth() >= 3 ? t.getFullYear() : t.getFullYear() - 1; return { from: `${s}-04-01`, to: `${s + 1}-03-31` }; };
const fmtDate = (s?: string | null) => (s ? new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

const ChartOfAccountsWorkspace: React.FC = () => {
  const navigate = useNavigate();
  const c = useControlRoomColors();
  const line = `1px solid ${c.line}`;
  const panelSx = { borderRadius: 3, border: line, bgcolor: c.panel } as const;
  const labelSx = { fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' as const, color: c.sub };

  const [accounts, setAccounts] = useState<CoaAccount[]>([]);
  const [summary, setSummary] = useState<CoaSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [view, setView] = useState<'tree' | 'list'>('tree');

  const [fType, setFType] = useState('');
  const [fStatus, setFStatus] = useState('active');
  const [fGroup, setFGroup] = useState('');
  const [q, setQ] = useState('');
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detailTab, setDetailTab] = useState(0);
  const [gl, setGl] = useState<EnhancedGeneralLedger | null>(null);
  const [glLoading, setGlLoading] = useState(false);

  const [addAnchor, setAddAnchor] = useState<null | HTMLElement>(null);
  const [dialog, setDialog] = useState<{ mode: 'new' | 'sub' | 'edit'; account?: CoaAccount } | null>(null);
  const [validation, setValidation] = useState<CoaValidation | null>(null);
  const [validateOpen, setValidateOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([coaService.summary(), coaService.accounts()])
      .then(([s, a]) => { setSummary(s); setAccounts(a.accounts); setError(null); })
      .catch((e) => setError(e.message || 'Failed to load chart of accounts'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const byId = useMemo(() => new Map(accounts.map((a) => [a.id, a])), [accounts]);
  const childrenOf = useCallback((id: number | null) => accounts.filter((a) => a.parent_id === id), [accounts]);
  const roots = useMemo(() => accounts.filter((a) => !a.parent_id || !byId.has(a.parent_id)), [accounts, byId]);
  const groups = useMemo(() => Array.from(new Set(accounts.map((a) => a.group))).sort(), [accounts]);

  const selected = selectedId ? byId.get(selectedId) || null : null;

  useEffect(() => {
    if (!selected) { setGl(null); return; }
    setGlLoading(true);
    const { from, to } = fyRange();
    financeService.getEnhancedGeneralLedger(selected.id, from, to)
      .then(setGl).catch(() => setGl(null)).finally(() => setGlLoading(false));
  }, [selected]);

  // ---- filtering + tree flatten (search auto-expands ancestors) -------------
  const filterActive = !!(fType || fStatus !== 'all' || fGroup || q);
  const matches = useCallback((a: CoaAccount) => {
    if (fType && a.account_type !== fType) return false;
    if (fStatus === 'active' && !a.is_active) return false;
    if (fStatus === 'inactive' && a.is_active) return false;
    if (fGroup && a.group !== fGroup) return false;
    if (q) { const s = q.toLowerCase(); if (![a.account_code, a.account_name, a.group, a.account_type, ...a.mappings].some((x) => String(x).toLowerCase().includes(s))) return false; }
    return true;
  }, [fType, fStatus, fGroup, q]);

  const showSet = useMemo(() => {
    if (!filterActive) return null;
    const set = new Set<number>();
    accounts.filter(matches).forEach((a) => {
      set.add(a.id);
      let cur: CoaAccount | undefined = a, guard = 0;
      while (cur?.parent_id && byId.get(cur.parent_id) && guard < 20) { set.add(cur.parent_id); cur = byId.get(cur.parent_id); guard += 1; }
    });
    return set;
  }, [accounts, matches, filterActive, byId]);

  const flatTree = useMemo(() => {
    const rows: { a: CoaAccount; depth: number }[] = [];
    const walk = (a: CoaAccount, depth: number) => {
      if (showSet && !showSet.has(a.id)) return;
      rows.push({ a, depth });
      const kids = childrenOf(a.id);
      const isOpen = showSet ? true : !!expanded[a.id];
      if (kids.length && isOpen) kids.forEach((k) => walk(k, depth + 1));
    };
    roots.forEach((r) => walk(r, 0));
    return rows;
  }, [roots, childrenOf, expanded, showSet]);

  const listRows = useMemo(() => accounts.filter(matches).sort((a, b) => a.account_code.localeCompare(b.account_code, undefined, { numeric: true })), [accounts, matches]);

  const expandAll = () => setExpanded(Object.fromEntries(accounts.filter((a) => a.isParent).map((a) => [a.id, true])));
  const collapseAll = () => setExpanded({});

  const runValidate = () => { coaService.validation().then((v) => { setValidation(v); setValidateOpen(true); }).catch((e) => setToast(e.message || 'Validation failed')); };

  // ---- KPI + type summary ---------------------------------------------------
  const kpis = summary ? [
    { t: 'Total', v: summary.totalAccounts, tint: '#6366f1' }, { t: 'Active', v: summary.active, tint: '#059669' },
    { t: 'Inactive', v: summary.inactive, tint: '#dc2626' }, { t: 'Parent', v: summary.parent, tint: '#0284c7' },
    { t: 'Leaf', v: summary.leaf, tint: '#7c3aed' }, { t: 'System', v: summary.system, tint: '#0891b2' },
    { t: 'Custom', v: summary.custom, tint: '#ea580c' }, { t: 'Unused', v: summary.unused, tint: '#d97706' },
  ] : [];
  const typeSummary = summary ? [
    { t: 'Assets', k: 'ASSET' }, { t: 'Liabilities', k: 'LIABILITY' }, { t: 'Equity', k: 'EQUITY' },
    { t: 'Revenue', k: 'INCOME' }, { t: 'Expenses', k: 'EXPENSE' },
  ].map((x) => ({ ...x, count: summary.byType.find((b) => b.type === x.k)?.count || 0, tint: TYPE_TINT[x.k] })) : [];

  return (
    <Box sx={{ p: 3, bgcolor: c.bg, minHeight: '100%' }}>
      {/* header */}
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={2} flexWrap="wrap" sx={{ mb: 2.5 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>Chart of Accounts <Box component="span" sx={{ color: c.sub, fontWeight: 500 }}>— Indian Standard</Box></Typography>
          <Typography variant="body2" color="text.secondary">Manage your accounting structure, control accounts and financial mappings.</Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, v) => v && setView(v)}>
            <ToggleButton value="tree" sx={{ px: 1.5, textTransform: 'none' }}><AccountTree sx={{ fontSize: 17, mr: 0.5 }} />Tree</ToggleButton>
            <ToggleButton value="list" sx={{ px: 1.5, textTransform: 'none' }}><ViewList sx={{ fontSize: 17, mr: 0.5 }} />List</ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title="Refresh"><IconButton onClick={load} sx={{ border: line }}><Refresh fontSize="small" /></IconButton></Tooltip>
          <Button variant="outlined" size="small" startIcon={<FactCheck />} onClick={runValidate}>Validate COA</Button>
          <Button variant="contained" size="small" startIcon={<Add />} endIcon={<KeyboardArrowDown />} onClick={(e) => setAddAnchor(e.currentTarget)}>Add Account</Button>
          <Menu anchorEl={addAnchor} open={!!addAnchor} onClose={() => setAddAnchor(null)}>
            <MenuItem onClick={() => { setAddAnchor(null); navigate('/finance/accounts/new'); }}>New Account</MenuItem>
            <MenuItem onClick={() => { setAddAnchor(null); navigate(`/finance/accounts/new?parent=${selected?.id || ''}`); }} disabled={!selected}>New Sub-Account{selected ? ` under ${selected.account_code}` : ''}</MenuItem>
          </Menu>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* KPI health strip */}
      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
        {(summary ? kpis : Array.from({ length: 8 })).map((k: any, i) => (
          <Grid size={{ xs: 3, sm: 3, md: 1.5 }} key={i}>
            <Paper elevation={0} sx={{ ...panelSx, p: 1.5, height: '100%', background: summary ? `linear-gradient(160deg, ${k.tint}0d 0%, transparent 70%)` : undefined }}>
              {summary ? <>
                <Typography sx={{ fontWeight: 800, fontSize: '1.5rem', color: k.tint, lineHeight: 1 }}>{k.v}</Typography>
                <Typography sx={{ ...labelSx, mt: 0.5 }}>{k.t}</Typography>
              </> : <CircularProgress size={18} />}
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* type summary */}
      <Stack direction="row" spacing={1.5} sx={{ mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
        {typeSummary.map((t) => (
          <Paper key={t.k} elevation={0} onClick={() => setFType(fType === t.k ? '' : t.k)}
            sx={{ ...panelSx, px: 2, py: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1.25, borderColor: fType === t.k ? t.tint : c.line }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: t.tint }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{t.t}</Typography>
            <Typography variant="body2" sx={{ fontWeight: 800, color: t.tint }}>{t.count}</Typography>
          </Paper>
        ))}
      </Stack>

      {/* filter bar */}
      <Paper elevation={0} sx={{ ...panelSx, p: 1.5, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField select size="small" label="Type" value={fType} onChange={(e) => setFType(e.target.value)} sx={{ minWidth: 140 }}>
            <MenuItem value="">All Types</MenuItem>
            {['ASSET', 'LIABILITY', 'EQUITY', 'INCOME', 'EXPENSE'].map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Status" value={fStatus} onChange={(e) => setFStatus(e.target.value)} sx={{ minWidth: 130 }}>
            <MenuItem value="all">All</MenuItem><MenuItem value="active">Active</MenuItem><MenuItem value="inactive">Inactive</MenuItem>
          </TextField>
          <TextField select size="small" label="Group" value={fGroup} onChange={(e) => setFGroup(e.target.value)} sx={{ minWidth: 180 }}>
            <MenuItem value="">All Groups</MenuItem>
            {groups.map((g) => <MenuItem key={g} value={g}>{g}</MenuItem>)}
          </TextField>
          <TextField size="small" placeholder="Search code, name, group, GST, bank…" value={q} onChange={(e) => setQ(e.target.value)}
            sx={{ minWidth: 260, flexGrow: 1 }} InputProps={{ startAdornment: <Search sx={{ fontSize: 18, color: c.sub, mr: 0.5 }} /> }} />
          {(fType || fGroup || q || fStatus !== 'active') && <Button size="small" onClick={() => { setFType(''); setFGroup(''); setQ(''); setFStatus('active'); }}>Clear</Button>}
        </Box>
      </Paper>

      {loading ? <Box sx={{ display: 'grid', placeItems: 'center', py: 10 }}><CircularProgress /></Box> : (
        <Grid container spacing={2.5}>
          {/* hierarchy / list */}
          <Grid size={{ xs: 12, lg: 7 }}>
            <Paper elevation={0} sx={{ ...panelSx, overflow: 'hidden' }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.5, borderBottom: line }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography sx={{ fontWeight: 700 }}>{view === 'tree' ? 'Account Hierarchy' : 'Account List'}</Typography>
                  <Chip label={`${view === 'tree' ? flatTree.length : listRows.length} accounts`} size="small" sx={{ height: 20, fontSize: '0.6875rem', bgcolor: `${c.accent}1f`, color: c.accent }} />
                </Stack>
                {view === 'tree' && (
                  <Stack direction="row" spacing={0.5}>
                    <Button size="small" startIcon={<UnfoldMore sx={{ fontSize: 16 }} />} onClick={expandAll}>Expand All</Button>
                    <Button size="small" startIcon={<UnfoldLess sx={{ fontSize: 16 }} />} onClick={collapseAll}>Collapse All</Button>
                  </Stack>
                )}
              </Stack>

              <Box sx={{ maxHeight: 640, overflowY: 'auto' }}>
                {(view === 'tree' ? flatTree : listRows.map((a) => ({ a, depth: 0 }))).map(({ a, depth }) => {
                  const kids = childrenOf(a.id);
                  const isOpen = showSet ? true : !!expanded[a.id];
                  const isSel = selectedId === a.id;
                  const tint = TYPE_TINT[a.account_type];
                  return (
                    <Stack key={a.id} direction="row" alignItems="center" spacing={1}
                      onClick={() => setSelectedId(a.id)}
                      sx={{ px: 2, py: 1, borderBottom: line, cursor: 'pointer', bgcolor: isSel ? `${c.accent}0f` : 'transparent', borderLeft: isSel ? `3px solid ${c.accent}` : '3px solid transparent', '&:hover': { bgcolor: isSel ? `${c.accent}0f` : c.panel2 } }}>
                      <Box sx={{ pl: view === 'tree' ? depth * 2.5 : 0, display: 'flex', alignItems: 'center' }}>
                        {view === 'tree' && kids.length > 0 ? (
                          <IconButton size="small" sx={{ p: 0.25 }} onClick={(e) => { e.stopPropagation(); setExpanded((s) => ({ ...s, [a.id]: !isOpen })); }}>
                            {isOpen ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
                          </IconButton>
                        ) : <Box sx={{ width: view === 'tree' ? 28 : 0 }} />}
                        <AccountBalance sx={{ fontSize: 17, color: tint, ml: 0.25 }} />
                      </Box>
                      <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.8125rem', color: tint, minWidth: 68 }}>{a.account_code}</Typography>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.account_name}</Typography>
                        {view === 'list' && <Typography variant="caption" color="text.secondary">{a.group}</Typography>}
                      </Box>
                      <Box component="span" sx={{ px: 0.75, py: 0.25, borderRadius: 0.75, fontSize: '0.625rem', fontWeight: 700, color: tint, bgcolor: `${tint}14`, display: { xs: 'none', sm: 'inline-block' } }}>{a.account_type}</Box>
                      {a.used
                        ? <Tooltip title={`${a.txnCount} transactions · last ${fmtDate(a.lastUsed)}`}><Box component="span" sx={{ px: 0.75, py: 0.25, borderRadius: 0.75, fontSize: '0.5625rem', fontWeight: 700, color: c.good, bgcolor: `${c.good}14`, display: { xs: 'none', md: 'inline-block' } }}>USED · {a.txnCount}</Box></Tooltip>
                        : <Box component="span" sx={{ px: 0.75, py: 0.25, borderRadius: 0.75, fontSize: '0.5625rem', fontWeight: 700, color: c.sub, bgcolor: `${c.sub}14`, display: { xs: 'none', md: 'inline-block' } }}>UNUSED</Box>}
                      {a.is_default && <Tooltip title="System-controlled account"><Lock sx={{ fontSize: 14, color: c.sub }} /></Tooltip>}
                      <StatusDot label={a.is_active ? 'Active' : 'Inactive'} tone={a.is_active ? 'success' : 'neutral'} />
                    </Stack>
                  );
                })}
                {(view === 'tree' ? flatTree.length : listRows.length) === 0 && (
                  <Box sx={{ py: 6, textAlign: 'center' }}><Typography variant="body2" color="text.secondary">No accounts match the current filters.</Typography></Box>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* details panel */}
          <Grid size={{ xs: 12, lg: 5 }}>
            {!selected ? (
              <Paper elevation={0} sx={{ ...panelSx, py: 8, textAlign: 'center', height: '100%' }}>
                <Hub sx={{ fontSize: 40, color: c.sub, mb: 1 }} />
                <Typography variant="body1" sx={{ fontWeight: 700 }}>Select an account</Typography>
                <Typography variant="body2" color="text.secondary">Pick an account to see its structure, controls, balance, mappings and dependencies.</Typography>
              </Paper>
            ) : (
              <Paper elevation={0} sx={{ ...panelSx, position: { lg: 'sticky' }, top: 88, overflow: 'hidden' }}>
                <Box sx={{ p: 2.5, borderBottom: line }}>
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                        <Box component="span" sx={{ fontFamily: 'monospace', color: TYPE_TINT[selected.account_type], mr: 1 }}>{selected.account_code}</Box>{selected.account_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{selected.group} · {selected.account_type}</Typography>
                    </Box>
                    {selected.is_default && <Chip icon={<Lock sx={{ fontSize: '13px !important' }} />} label="System" size="small" sx={{ height: 22, fontSize: '0.6875rem', bgcolor: `${c.sub}1f`, color: c.sub }} />}
                  </Stack>
                  <Button fullWidth variant="outlined" size="small" startIcon={<ReceiptLong />} sx={{ mt: 1.5 }} component={RouterLink} to={`/finance/ledger?account=${selected.id}`}>View Account Statement</Button>
                </Box>

                <Tabs value={detailTab} onChange={(_, v) => setDetailTab(v)} variant="scrollable" scrollButtons="auto" sx={{ borderBottom: line, px: 1, minHeight: 42, '& .MuiTab-root': { minHeight: 42, textTransform: 'none', fontWeight: 600, minWidth: 0, px: 1.5 } }}>
                  <Tab label="Overview" /><Tab label="Balance" /><Tab label="Transactions" /><Tab label="Mappings" /><Tab label="Usage" />
                </Tabs>

                <Box sx={{ p: 2.5, maxHeight: 520, overflowY: 'auto' }}>
                  {detailTab === 0 && (
                    <Stack spacing={2}>
                      <Grid container spacing={1.5}>
                        {([['Account Type', selected.account_type], ['Account Group', selected.group], ['Parent Account', selected.parent_id ? (byId.get(selected.parent_id)?.account_name || '—') : '—'], ['Level', String(selected.level)], ['Normal Balance', selected.normalBalance === 'Dr' ? 'Debit' : 'Credit'], ['Currency', selected.controls.currency], ['System Account', selected.is_default ? 'Yes' : 'No'], ['Status', selected.is_active ? 'Active' : 'Inactive']] as [string, string][]).map(([l, v]) => (
                          <Grid size={6} key={l}><Typography sx={labelSx}>{l}</Typography><Typography variant="body2" sx={{ fontWeight: 700 }}>{v}</Typography></Grid>
                        ))}
                      </Grid>
                      <Divider sx={{ borderColor: c.line }} />
                      <Typography sx={{ fontWeight: 700, fontSize: '0.8125rem' }}>Accounting Controls <Typography component="span" variant="caption" color="text.secondary">(derived)</Typography></Typography>
                      <Grid container spacing={1}>
                        {([['Allow Manual Posting', selected.controls.allowManualPosting], ['Control Account', selected.controls.controlAccount], ['Cost Center Required', selected.controls.costCenterRequired], ['Reconciliation', selected.controls.reconciliationEnabled], ['GST Applicable', selected.controls.gstApplicable], ['TDS Applicable', selected.controls.tdsApplicable], ['Bank Account', selected.controls.bankAccount]] as [string, boolean][]).map(([l, v]) => (
                          <Grid size={6} key={l}><Stack direction="row" alignItems="center" spacing={0.75}>{v ? <CheckCircle sx={{ fontSize: 15, color: c.good }} /> : <Cancel sx={{ fontSize: 15, color: c.sub }} />}<Typography variant="caption" sx={{ fontWeight: 600 }}>{l}</Typography></Stack></Grid>
                        ))}
                      </Grid>
                      <Divider sx={{ borderColor: c.line }} />
                      <Stack direction="row" spacing={1}>
                        <Button size="small" variant="outlined" disabled={selected.is_default} onClick={() => navigate(`/finance/accounts/${selected.id}/edit`)}>Edit</Button>
                        <Button size="small" variant="outlined" color="warning" disabled={selected.is_default || selected.used}
                          onClick={() => financeService.updateAccount(selected.id, { is_active: false } as any).then(() => { setToast('Account deactivated'); load(); }).catch((e) => setToast(e.message))}>Deactivate</Button>
                        <Button size="small" onClick={() => navigate(`/finance/accounts/new?parent=${selected.id}`)}>Add Sub-Account</Button>
                      </Stack>
                      {selected.is_default && <Alert severity="info" sx={{ borderRadius: 2 }}>This account is system-controlled because it is referenced by GST / Treasury / AP automation. Editing is restricted.</Alert>}
                    </Stack>
                  )}

                  {detailTab === 1 && (
                    glLoading ? <Box sx={{ display: 'grid', placeItems: 'center', py: 4 }}><CircularProgress size={22} /></Box>
                      : gl ? (
                        <Stack spacing={1.5}>
                          <Grid container spacing={1.5}>
                            {[['Opening', `${formatCurrency(gl.openingBalance.amount)} ${gl.openingBalance.type}`], ['Total Debit', formatCurrency(gl.totals.totalDebit)], ['Total Credit', formatCurrency(gl.totals.totalCredit)], ['Closing', `${formatCurrency(gl.closingBalance.amount)} ${gl.closingBalance.type}`], ['Transactions', String(gl.count)], ['Last Activity', fmtDate(gl.lastTransactionDate)]].map(([l, v]) => (
                              <Grid size={6} key={l}><Typography sx={labelSx}>{l}</Typography><Typography variant="body2" sx={{ fontWeight: 700 }}>{v}</Typography></Grid>
                            ))}
                          </Grid>
                          {gl.monthly && gl.monthly.length > 0 && (
                            <Box sx={{ pt: 1 }}>
                              <Typography sx={{ ...labelSx, mb: 1 }}>Monthly Movement</Typography>
                              {gl.monthly.map((m) => {
                                const max = Math.max(...gl.monthly!.map((x) => Math.max(x.debit, x.credit)), 1);
                                return (
                                  <Stack key={m.month} direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
                                    <Typography variant="caption" sx={{ width: 56, color: c.sub }}>{new Date(Number(m.month.split('-')[0]), Number(m.month.split('-')[1]) - 1, 1).toLocaleDateString('en-IN', { month: 'short' })}</Typography>
                                    <Box sx={{ flex: 1, display: 'flex', gap: 0.5 }}>
                                      <Box sx={{ height: 14, width: `${(m.debit / max) * 100}%`, bgcolor: '#059669', borderRadius: 0.5, minWidth: m.debit ? 2 : 0 }} />
                                      <Box sx={{ height: 14, width: `${(m.credit / max) * 100}%`, bgcolor: '#dc2626', borderRadius: 0.5, minWidth: m.credit ? 2 : 0 }} />
                                    </Box>
                                    <Typography variant="caption" sx={{ width: 84, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatAmount(Math.abs(m.net))}</Typography>
                                  </Stack>
                                );
                              })}
                              <Stack direction="row" spacing={2} sx={{ mt: 1 }}><Legend c={c} color="#059669" label="Debit" /><Legend c={c} color="#dc2626" label="Credit" /></Stack>
                            </Box>
                          )}
                        </Stack>
                      ) : <Typography variant="body2" color="text.secondary">No balance data for this account in the current FY.</Typography>
                  )}

                  {detailTab === 2 && (
                    glLoading ? <Box sx={{ display: 'grid', placeItems: 'center', py: 4 }}><CircularProgress size={22} /></Box>
                      : gl && gl.entries.length > 0 ? (
                        <TableContainer><Table size="small" sx={{ '& td, & th': { borderColor: c.line } }}>
                          <TableHead><TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.625rem', textTransform: 'uppercase', color: c.sub } }}>
                            <TableCell>Date / Voucher</TableCell><TableCell align="right">Debit</TableCell><TableCell align="right">Credit</TableCell><TableCell align="right">Balance</TableCell>
                          </TableRow></TableHead>
                          <TableBody>
                            {gl.entries.slice(-12).reverse().map((e) => (
                              <TableRow key={e.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/finance/journal/${e.journal_entry_id ?? e.id}`)}>
                                <TableCell><Typography variant="caption" sx={{ fontWeight: 700, color: c.accent }}>{e.entry_number}</Typography><Typography variant="caption" color="text.secondary" display="block">{fmtDate(e.entry_date)}</Typography></TableCell>
                                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{e.debit_amount ? formatAmount(e.debit_amount) : '—'}</TableCell>
                                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{e.credit_amount ? formatAmount(e.credit_amount) : '—'}</TableCell>
                                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{formatAmount(e.running_balance)} {e.balance_type}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table></TableContainer>
                      ) : <Typography variant="body2" color="text.secondary">No transactions for this account in the current FY.</Typography>
                  )}

                  {detailTab === 3 && (
                    <Stack spacing={1.5}>
                      <Typography variant="body2" color="text.secondary">Modules that use this account (derived from account role and posted activity).</Typography>
                      {selected.mappings.length === 0 ? <Typography variant="body2">No module mappings detected.</Typography> : (
                        <Stack spacing={1}>
                          {selected.mappings.map((m) => (
                            <Stack key={m} direction="row" alignItems="center" spacing={1.25} sx={{ p: 1.25, borderRadius: 2, border: line }}>
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: MODULE_TINT[m] || c.accent }} />
                              <Typography variant="body2" sx={{ fontWeight: 700, flex: 1 }}>{m}</Typography>
                              <CheckCircle sx={{ fontSize: 16, color: c.good }} />
                            </Stack>
                          ))}
                        </Stack>
                      )}
                      <Divider sx={{ borderColor: c.line }} />
                      <Typography sx={{ ...labelSx }}>Dependencies</Typography>
                      <Typography variant="body2">Used by <b>{selected.mappings.length}</b> module{selected.mappings.length === 1 ? '' : 's'}{selected.isParent ? ` and has ${selected.childCount} sub-account${selected.childCount === 1 ? '' : 's'}` : ''}. {(selected.used || selected.isParent) && 'Deactivation is blocked while dependencies exist.'}</Typography>
                    </Stack>
                  )}

                  {detailTab === 4 && (
                    <Stack spacing={1.5}>
                      <Grid container spacing={1.5}>
                        {[['Usage', selected.used ? 'Used' : 'Unused'], ['Transactions', String(selected.txnCount)], ['Last Used', fmtDate(selected.lastUsed)], ['Total Debit', formatCurrency(selected.totalDebit)], ['Total Credit', formatCurrency(selected.totalCredit)], ['Current Balance', `${formatCurrency(selected.balance)} ${selected.balanceType}`], ['Sub-Accounts', String(selected.childCount)], ['Created', fmtDate(selected.created_at)], ['Last Updated', fmtDate(selected.updated_at)]].map(([l, v]) => (
                          <Grid size={6} key={l}><Typography sx={labelSx}>{l}</Typography><Typography variant="body2" sx={{ fontWeight: 700 }}>{v}</Typography></Grid>
                        ))}
                      </Grid>
                      {!selected.used && <Alert severity="info" sx={{ borderRadius: 2 }}>This account has no posted transactions — a candidate for review or deactivation.</Alert>}
                    </Stack>
                  )}
                </Box>
              </Paper>
            )}
          </Grid>
        </Grid>
      )}

      {/* validate dialog */}
      <Dialog open={validateOpen} onClose={() => setValidateOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>Validate Chart of Accounts</DialogTitle>
        <DialogContent dividers>
          {validation && <>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <Stat c={c} icon={<CheckCircle sx={{ color: c.good }} />} label="Passed" value={validation.summary.checksPassed} />
              <Stat c={c} icon={<WarningAmber sx={{ color: c.warn }} />} label="Warnings" value={validation.summary.warnings} />
              <Stat c={c} icon={<Cancel sx={{ color: c.bad }} />} label="Errors" value={validation.summary.errors} />
            </Stack>
            {validation.issues.length === 0 ? <Alert severity="success">All checks passed. No issues detected.</Alert> : (
              <Stack spacing={1}>
                {validation.issues.map((iss, i) => (
                  <Stack key={i} direction="row" spacing={1.25} sx={{ p: 1.25, borderRadius: 2, border: line }}>
                    {iss.severity === 'error' ? <Cancel sx={{ fontSize: 18, color: c.bad, mt: 0.25 }} /> : <WarningAmber sx={{ fontSize: 18, color: c.warn, mt: 0.25 }} />}
                    <Box><Typography variant="body2" sx={{ fontWeight: 700 }}>{iss.title}</Typography><Typography variant="caption" color="text.secondary">{iss.detail}</Typography></Box>
                  </Stack>
                ))}
              </Stack>
            )}
          </>}
        </DialogContent>
        <DialogActions><Button onClick={() => setValidateOpen(false)}>Close</Button></DialogActions>
      </Dialog>

      {/* add/edit dialog */}
      {dialog && <AccountDialog dialog={dialog} accounts={accounts} onClose={() => setDialog(null)} onSaved={(msg) => { setDialog(null); setToast(msg); load(); }} />}

      <Snackbar open={!!toast} autoHideDuration={2600} onClose={() => setToast(null)} message={toast || ''} />
    </Box>
  );
};

const Legend: React.FC<{ c: any; color: string; label: string }> = ({ color, label }) => (
  <Stack direction="row" alignItems="center" spacing={0.5}><Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: color }} /><Typography variant="caption" color="text.secondary">{label}</Typography></Stack>
);
const Stat: React.FC<{ c: any; icon: React.ReactNode; label: string; value: number }> = ({ icon, label, value }) => (
  <Stack direction="row" alignItems="center" spacing={1}>{icon}<Box><Typography sx={{ fontWeight: 800, fontSize: '1.25rem', lineHeight: 1 }}>{value}</Typography><Typography variant="caption" color="text.secondary">{label}</Typography></Box></Stack>
);

// ---- add / edit dialog (reuses existing create/update APIs) -----------------
const AccountDialog: React.FC<{ dialog: { mode: 'new' | 'sub' | 'edit'; account?: CoaAccount }; accounts: CoaAccount[]; onClose: () => void; onSaved: (m: string) => void }> = ({ dialog, accounts, onClose, onSaved }) => {
  const c = useControlRoomColors();
  const editing = dialog.mode === 'edit';
  const [code, setCode] = useState(editing ? dialog.account!.account_code : '');
  const [name, setName] = useState(editing ? dialog.account!.account_name : '');
  const [type, setType] = useState<CoaAccount['account_type']>(editing ? dialog.account!.account_type : (dialog.account?.account_type || 'ASSET'));
  const [parentId, setParentId] = useState<string>(dialog.mode === 'sub' && dialog.account ? String(dialog.account.id) : '');
  const [isActive, setIsActive] = useState(editing ? dialog.account!.is_active : true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const dupCode = !editing && code && accounts.some((a) => a.account_code.toLowerCase() === code.toLowerCase());
  const normal = ['ASSET', 'EXPENSE'].includes(type) ? 'Debit' : 'Credit';

  const save = async () => {
    setErr('');
    if (!editing && !code.trim()) return setErr('Account code is required');
    if (!name.trim()) return setErr('Account name is required');
    if (dupCode) return setErr('Account code already exists');
    setSaving(true);
    try {
      if (editing) await financeService.updateAccount(dialog.account!.id, { account_name: name, is_active: isActive } as any);
      else await financeService.createAccount({ account_code: code, account_name: name, account_type: type, parent_id: parentId ? Number(parentId) : undefined, is_active: isActive } as any);
      onSaved(editing ? 'Account updated' : 'Account created');
    } catch (e: any) { setErr(e.message || 'Failed to save account'); } finally { setSaving(false); }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 800 }}>{editing ? 'Edit Account' : dialog.mode === 'sub' ? 'New Sub-Account' : 'New Account'}</DialogTitle>
      <DialogContent dividers>
        {err && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{err}</Alert>}
        <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: c.sub, mb: 1 }}>Basic Information</Typography>
        <Grid container spacing={2}>
          <Grid size={6}><TextField fullWidth size="small" label="Account Code *" value={code} disabled={editing} error={!!dupCode} helperText={dupCode ? 'Code already exists' : ''} onChange={(e) => setCode(e.target.value)} /></Grid>
          <Grid size={6}><TextField select fullWidth size="small" label="Account Type *" value={type} disabled={editing} onChange={(e) => setType(e.target.value as any)}>{['ASSET', 'LIABILITY', 'EQUITY', 'INCOME', 'EXPENSE'].map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}</TextField></Grid>
          <Grid size={12}><TextField fullWidth size="small" label="Account Name *" value={name} onChange={(e) => setName(e.target.value)} /></Grid>
          <Grid size={6}>
            <TextField select fullWidth size="small" label="Parent Account" value={parentId} disabled={editing} onChange={(e) => setParentId(e.target.value)}>
              <MenuItem value=""><em>None (top level)</em></MenuItem>
              {accounts.filter((a) => a.account_type === type && a.id !== dialog.account?.id).map((a) => <MenuItem key={a.id} value={a.id}>{a.account_code} — {a.account_name}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid size={6}><TextField select fullWidth size="small" label="Status" value={isActive ? '1' : '0'} onChange={(e) => setIsActive(e.target.value === '1')}><MenuItem value="1">Active</MenuItem><MenuItem value="0">Inactive</MenuItem></TextField></Grid>
        </Grid>
        <Divider sx={{ my: 2, borderColor: c.line }} />
        <Stack direction="row" spacing={3}>
          <Box><Typography variant="caption" color="text.secondary">Normal Balance (derived)</Typography><Typography variant="body2" sx={{ fontWeight: 700 }}>{normal}</Typography></Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={saving} onClick={save}>{editing ? 'Save Changes' : 'Create Account'}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChartOfAccountsWorkspace;
